import { useFormState } from "react-final-form";
import { omit } from "lodash";

import { FondStyleType, Layer, LayerClassification, StyleWithoutID } from "fond/types";

import { IClassifyFormData } from "./ClassifyForm";
import { IDefaultStyleFormData } from "./StyleSettings";

/**
 * Generates a NewStyle object that can be used to create new styles based on a
 * layerClassification.
 *
 * @param layerClassification that determines the type of style created & the colors.
 * @returns a newStyle object.
 */
export const generateStyleFromClassification = ({
  attributeId,
  layerClassification,
  colors,
}: {
  attributeId: string;
  layerClassification: LayerClassification;
  colors: string[];
}): StyleWithoutID => {
  let newStyle: StyleWithoutID = {
    ID: null,
    ConfigurationID: null,
    ConfigurationType: "SUBLAYER",
    Name: "Style",
    Position: 0,
  };
  const minStop = colors.length > 0 ? getRangeValue(layerClassification, 0) : 0;
  const maxStop = colors.length > 0 ? getRangeValue(layerClassification, 1) : 0;
  const isInterpolate =
    colors.length > 1 && typeof minStop === "number" && typeof maxStop === "number" && layerClassification.FilterConfiguration.Type !== "other";
  const color = layerClassification.FilterConfiguration.Type !== "other" ? colors[0] : "#ccc";

  // Generate interpolation stops if we have multiple colors
  const generateStops = (): Array<[number, string]> => {
    if (!isInterpolate || typeof minStop !== "number" || typeof maxStop !== "number") {
      return [];
    }

    // Create evenly distributed stops between min and max
    return colors.map((colorValue, index) => {
      const value = minStop + ((maxStop - minStop) * index) / (colors.length - 1);
      return [value, colorValue];
    });
  };

  if (layerClassification.GeometryType === "Point") {
    newStyle = {
      ...newStyle,
      Type: "circle",
      MapboxStyle: { type: "circle" },
      ...(isInterpolate
        ? {
            CircleColorInterpolate: {
              property: attributeId,
              stops: generateStops(),
            },
          }
        : { CircleColor: color }),
    };
  }

  if (layerClassification.GeometryType === "LineString") {
    newStyle = {
      ...newStyle,
      Type: "line",
      MapboxStyle: { type: "line" },
      ...(isInterpolate
        ? {
            LineColorInterpolate: {
              property: attributeId,
              stops: generateStops(),
            },
          }
        : { LineColor: color }),
      LineWidth: 3,
    };
  }

  if (layerClassification.GeometryType === "Polygon") {
    newStyle = {
      ...newStyle,
      Type: "fill",
      MapboxStyle: { type: "fill" },
      ...(isInterpolate
        ? {
            FillColorInterpolate: {
              property: attributeId,
              stops: generateStops(),
            },
          }
        : { FillColor: color }),
    };
  }

  return newStyle;
};

/**
 * Maps a Layer Geometry to a Fond Style Type
 */
export const getFondStyleTypeFromGeometry = (type: Layer["Geometry"]): FondStyleType => {
  if (type === null) return "circle";

  const map: Record<typeof type, FondStyleType> = {
    LineString: "line",
    MultiLineString: "line",
    MultiPoint: "circle",
    MultiPolygon: "fill",
    Point: "circle",
    Polygon: "fill",
    Null: "circle",
  };

  return map[type];
};

/**
 * Gets the styles values from the react-final-form values
 */
export const useDefaultStyle = (): Partial<IDefaultStyleFormData> => {
  const { values } = useFormState<IClassifyFormData>();

  return getDefaultStyleValues(values);
};

export const getDefaultStyleValues = (values: IClassifyFormData): Partial<IDefaultStyleFormData> =>
  omit(values, "attributeId", "colorRamp", "groupSize", "range", "layerClassifications", "invertColorRamp", "gradientColors");

export const getRangeValue = (data: LayerClassification | undefined, expressionIndex: number): string | number => {
  const filterConfig = data?.FilterConfiguration;
  if (!filterConfig) return "";

  if (filterConfig.Type === "other") {
    return "Other";
  } else if (filterConfig.Type === "group" && filterConfig.Expressions && filterConfig.Expressions.length > expressionIndex) {
    return filterConfig.Expressions[expressionIndex].ValueKey;
  }
  return "";
};
